import React, { ReactElement, ReactNode, useMemo, useRef } from 'react'
import { SimpleActionMenuItemProps } from 'ui'
import DeviceSearchDropdown from 'components/Dashboard/DeviceDropdown/DeviceSearchDropdown'
import { useLocation } from '@reach/router'
import DeviceDropdownItem from './DeviceDropdownItem'
import { DeviceInfo } from 'store/api/devices/devices.interface'
import { ThemeUIStyleObject } from 'theme-ui'
import { useHideTooltipOnScroll } from 'utils/useHideTooltipOnScroll'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import { scrollingContainerRef } from 'components/Dashboard/DashboardScrollingContainer'
import { scrollingFiltersContainerRef } from 'ui/CommonLogComponents/FiltersScrollingContainer'

export const allEndpoints = 'All Endpoints'

export interface DevicesDropdownProps {
  deviceId?: string
  dropdownDevices: DeviceInfo[]
  isAllDevicesVisible?: boolean
  shouldShowClients?: boolean
  onClick?: (deviceId: string, deviceName?: string) => void
  boundaryElementTestId?: string
  maxWidth?: string
  isLoading?: boolean
  shouldShowSmallView?: boolean
  sxButtonContent?: ThemeUIStyleObject
  sxDropdownContent?: ThemeUIStyleObject
  sx?: ThemeUIStyleObject
  shouldHighlightSelectedEndpoint?: boolean
  placeholder?: string | ReactNode
  defaultArrowColor?: string
  shouldUseAnalyticsDisabled?: boolean
}

export default function DeviceDropdown({
  isAllDevicesVisible = true,
  deviceId,
  shouldShowClients = true,
  dropdownDevices,
  onClick,
  boundaryElementTestId,
  maxWidth,
  isLoading,
  shouldShowSmallView,
  sxButtonContent,
  sxDropdownContent,
  sx,
  shouldHighlightSelectedEndpoint,
  placeholder,
  defaultArrowColor,
  shouldUseAnalyticsDisabled,
}: DevicesDropdownProps): ReactElement | null {
  const location = useLocation()
  const isProfiles = location.pathname.includes('profiles')
  const isMobile = useBreakpointIndex() === 0

  const hideRef = useRef<() => void>()

  useHideTooltipOnScroll(hideRef, isMobile ? scrollingFiltersContainerRef : scrollingContainerRef)

  const deviceName =
    dropdownDevices.find(
      device => device.device_id === deviceId || device.resolvers.uid === deviceId,
    )?.name || (isAllDevicesVisible ? allEndpoints : undefined)

  const dropdownButtonText =
    deviceId !== undefined || isAllDevicesVisible
      ? deviceName || placeholder
      : `${dropdownDevices.length} Endpoint${dropdownDevices.length > 1 ? 's' : ''}`

  const options: SimpleActionMenuItemProps[] = useMemo(() => {
    const devicesOptions = dropdownDevices.map(device => ({
      isSelected: deviceId === device.PK,
      ariaLabel: `${device.name} option`,
      children: (
        <DeviceDropdownItem
          isSelected={deviceId === device.PK}
          isGraphIconVisible={isProfiles}
          icon={device.icon}
          text={device.name}
          stats={device.stats}
          status={device.status}
          pk={device.PK}
          lastActivity={device.last_activity}
          parentDeviceId={device.parent_device?.device_id}
          clientsCount={shouldShowClients ? device.clients?.length : undefined}
          testId={`${device.name}-device-dropdown-item`}
          shouldUseAnalyticsDisabled={shouldUseAnalyticsDisabled}
        />
      ),
      onClick: () => {
        onClick?.(device.device_id, device.name)
        hideRef.current?.()
      },
    }))

    return isAllDevicesVisible
      ? [
          {
            ariaLabel: `all devices option`,
            isSelected: !deviceId && isAllDevicesVisible,
            children: (
              <DeviceDropdownItem
                text={allEndpoints}
                isSelected={!deviceId && isAllDevicesVisible}
              />
            ),
            onClick: () => {
              onClick?.('')
              hideRef.current?.()
            },
          },
          ...devicesOptions,
        ]
      : devicesOptions
  }, [
    dropdownDevices,
    isAllDevicesVisible,
    deviceId,
    isProfiles,
    shouldShowClients,
    shouldUseAnalyticsDisabled,
    onClick,
  ])

  if (isLoading) {
    return null
  }

  return (
    <DeviceSearchDropdown
      hideRef={hideRef}
      dropdownButtonText={dropdownButtonText}
      isDisabled={!dropdownDevices.length}
      isSelectable={true}
      ariaLabel="endpoints menu"
      testId="devices-dropdown-button"
      options={options}
      boundaryElementTestId={boundaryElementTestId || 'dashboard-scrolling-container'}
      searchPlaceholder="Search Endpoints"
      maxWidth={maxWidth}
      shouldShowSmallView={shouldShowSmallView}
      sxButtonContent={sxButtonContent}
      sxDropdownContent={sxDropdownContent}
      sx={sx}
      shouldHighlightSelectedEndpoint={shouldHighlightSelectedEndpoint}
      defaultArrowColor={defaultArrowColor}
    />
  )
}
